import type { UseCallTimerReturn } from './index.types';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useEffect, useState, useCallback } from 'react';
import { CALL_TIMER_INTERVAL_MS } from './index.constants';

/**
 * Hook for tracking call duration
 *
 * Calculates duration from the call's answerTime when the call state
 * is 'active' or 'held'. Updates every second.
 *
 * IMPORTANT: Uses Zustand selectors to ensure state is always fresh.
 */
export const useCallTimer = (): UseCallTimerReturn => {
  const [duration, setDuration] = useState(0);

  // Derive currentCall directly from selector (avoids stale state)
  const currentCall = useSoftphoneStore((state) =>
    state.currentCallId
      ? state.activeCalls.get(state.currentCallId) ?? null
      : null
  );

  const isRunning =
    currentCall?.state === 'active' || currentCall?.state === 'held';

  const answerTime = currentCall?.answerTime;

  const reset = useCallback(() => {
    setDuration(0);
  }, []);

  useEffect(() => {
    if (!isRunning || !answerTime) {
      return;
    }

    const calculateDuration = () => {
      const start = new Date(answerTime).getTime();
      const now = Date.now();
      return Math.floor((now - start) / CALL_TIMER_INTERVAL_MS);
    };

    // Set initial duration
    setDuration(calculateDuration());

    // Update every second
    const intervalId = setInterval(() => {
      setDuration(calculateDuration());
    }, CALL_TIMER_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isRunning, answerTime]);

  useEffect(() => {
    if (!currentCall) {
      setDuration(0);
    }
  }, [currentCall]);

  return {
    duration,
    isRunning,
    reset,
  };
};
